import { useEffect, useRef, useState } from "react";
import { loadRnnoise, RnnoiseWorkletNode } from "@sapphi-red/web-noise-suppressor";
import { useFeatureFlag } from "../services/featureFlags";

export default function useNoiseControlledStream(
    rawStream: MediaStream | null,
    isEnabled: boolean
): MediaStream | null {
    const [processedStream, setProcessedStream] = useState<MediaStream | null>(null);
    const rnnoiseRef = useRef<RnnoiseWorkletNode | null>(null);
    const audioCtxRef = useRef<AudioContext | null>(null);
    const wasmBinaryRef = useRef<ArrayBuffer | null>(null);
    
    const rnnoiseEnabled = useFeatureFlag('rnnoise-noise-suppression');

    useEffect(() => {
        if (!rawStream) {
            setProcessedStream(null);
            return;
        }

        if (!rnnoiseEnabled || !isEnabled) {
            setProcessedStream(rawStream);
            return;
        }

        let cancelled = false;
        
        const initializeNoiseSuppression = async () => {
            try {
                const audioCtx = new AudioContext({ sampleRate: 48000 });
                
                if (cancelled) {
                    audioCtx.close();
                    return;
                }
                
                audioCtxRef.current = audioCtx;

                if (!wasmBinaryRef.current) {
                    wasmBinaryRef.current = await loadRnnoise({
                        url: "/rnnoise/rnnoise.wasm",
                        simdUrl: "/rnnoise/rnnoise_simd.wasm"
                    });
                }

                if (cancelled || (audioCtx.state as string) === 'closed') {
                    return;
                }

                await audioCtx.audioWorklet.addModule("/rnnoise/workletProcessor.js");

                if (cancelled || (audioCtx.state as string) === 'closed') {
                    return;
                }

                const source = audioCtx.createMediaStreamSource(rawStream);
                
                const rnnoise = new RnnoiseWorkletNode(audioCtx, {
                    wasmBinary: wasmBinaryRef.current,
                    maxChannels: 1
                });
                rnnoiseRef.current = rnnoise;

                const highpass = audioCtx.createBiquadFilter();
                highpass.type = 'highpass';
                highpass.frequency.value = 80;
                highpass.Q.value = 0.7;

                const compressor = audioCtx.createDynamicsCompressor();
                compressor.threshold.value = -24;
                compressor.ratio.value = 3;
                compressor.attack.value = 0.008;
                compressor.release.value = 0.2;
                compressor.knee.value = 12;

                const limiter = audioCtx.createDynamicsCompressor();
                limiter.threshold.value = -2;
                limiter.ratio.value = 20;
                limiter.attack.value = 0.002;
                limiter.release.value = 0.05;
                limiter.knee.value = 0;

                const makeupGain = audioCtx.createGain();
                makeupGain.gain.value = 1.4;

                const destination = audioCtx.createMediaStreamDestination();
                
                source.connect(rnnoise);
                rnnoise.connect(highpass);
                highpass.connect(compressor);
                compressor.connect(makeupGain);
                makeupGain.connect(limiter);
                limiter.connect(destination);

                if (!cancelled) {
                    setProcessedStream(destination.stream);
                }

            } catch (error) {
                console.error("[RNNoise] Failed to initialize noise suppression:", error);
                if (!cancelled) {
                    setProcessedStream(rawStream);
                }
            }
        };

        initializeNoiseSuppression();

        return () => {
            cancelled = true;
            
            if (rnnoiseRef.current) {
                try {
                    rnnoiseRef.current.destroy();
                    rnnoiseRef.current.disconnect();
                } catch (error) {
                }
                rnnoiseRef.current = null;
            }
            
            if (audioCtxRef.current) {
                try {
                    audioCtxRef.current.close();
                } catch (error) {
                }
                audioCtxRef.current = null;
            }
        };
    }, [rawStream, isEnabled, rnnoiseEnabled]);

    return processedStream;
}
